import React, {useEffect, useState, useCallback} from "react";
import {Alert, Button, Card, Col, Divider, Form, Input, Row, Space, Typography} from "antd";
import {QRCodeSVG} from "qrcode.react";
import {
    CheckOutlined,
    CopyOutlined,
    DeleteOutlined,
    EyeInvisibleOutlined,
    MobileOutlined,
    PlusOutlined,
    QrcodeOutlined,
    SearchOutlined,
    TeamOutlined,
    UserOutlined
} from "@ant-design/icons";
import AvatarDisplay from "../components/AvatarDisplay";

const { Title, Text} = Typography;

const RegisterPlayers = ({
                             availableAvatars,
                             api,
                             showSuccess,
                             showError,
                             showWarning,
                             showInfo,
                         }) => {
    // Stany dla danych grupy
    const [groupName, setGroupName] = useState("");
    const [playerNames, setPlayerNames] = useState([""]);
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [loading, setLoading] = useState(false);
    const [teamData, setTeamData] = useState(null);
    const [showQRCode, setShowQRCode] = useState(false);

    // Stany dla zarządzania identyfikatorem
    const [groupId, setGroupId] = useState("");
    const [manualGroupId, setManualGroupId] = useState("");
    const [hasGroupIdFromUrl, setHasGroupIdFromUrl] = useState(false);
    const [groupMode, setGroupMode] = useState("select"); // "select" | "create" | "loaded"

    const [form] = Form.useForm();

    // Wczytaj istniejące grupy
    const loadExistingGroups = useCallback(async () => {
        try {
            const groups = await api.getPlayers();
            console.log('Loaded groups:', groups?.length || 0);
        } catch (error) {
            console.error('Błąd ładowania grup:', error);
            showError('Błąd ładowania listy grup ❌');
        }
    }, [api, showError]);

    // Wczytaj konkretną grupę po ID
    const loadExistingGroup = useCallback(async (id) => {
        if (!id) return;

        setLoading(true);
        try {
            const player = await api.getPlayer(id);
            if (player) {
                setGroupName(player.groupName || "");
                setPlayerNames(player.playerNames || [""]);

                // Znajdź awatar
                const avatar = availableAvatars.find(a =>
                    a.numericId === player.avatar?.numericId ||
                    a.numericId === player.avatarId
                );
                setSelectedAvatar(avatar || null);

                showSuccess(`Wczytano grupę "${player.groupName}" ✅`);
            }
        } catch (error) {
            console.error('Błąd ładowania grupy:', error);
            showError(`Błąd ładowania grupy: ${error.message} ❌`);
        } finally {
            setLoading(false);
        }
    }, [api, availableAvatars, showError, showSuccess]);

    // Pobierz identyfikator grupy z URL lub ustaw tryb wyboru
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const groupIdFromUrl = urlParams.get('nfc'); // zachowujemy 'nfc' dla kompatybilności

        if (groupIdFromUrl) {
            setGroupId(groupIdFromUrl);
            setHasGroupIdFromUrl(true);
            setGroupMode("loaded");
            loadExistingGroup(groupIdFromUrl);
        } else {
            setHasGroupIdFromUrl(false);
            setGroupMode("select");
            loadExistingGroups();
        }
    }, [loadExistingGroup, loadExistingGroups]);

    // Wczytaj grupę po wprowadzonym ID
    const handleLoadGroup = async () => {
        if (!manualGroupId.trim()) {
            showWarning('Proszę wprowadzić Identyfikator Grupy! 📝');
            return;
        }

        setGroupId(manualGroupId.trim());
        setGroupMode("loaded");
        await loadExistingGroup(manualGroupId.trim());
    };

    // Przejdź do trybu tworzenia nowej grupy
    const handleCreateNewGroup = () => {
        const newId = `${Math.random().toString(10).substr(2, 11)}`;
        setGroupId(newId);
        setGroupMode("create");
        setGroupName("");
        setPlayerNames([""]);
        setSelectedAvatar(null);
        showInfo('Tworzenie nowej grupy 🆕');
    };

    // Wróć do trybu wyboru
    const handleBackToSelect = () => {
        setGroupMode("select");
        setManualGroupId("");
        setGroupId("");
        setGroupName("");
        setPlayerNames([""]);
        setSelectedAvatar(null);
        loadExistingGroups();
    };

    const addPlayerField = () => {
        if (playerNames.length < 6) {
            setPlayerNames([...playerNames, ""]);
        } else {
            showWarning("Maksymalnie 6 graczy w grupie! 👥");
        }
    };

    const removePlayerField = (index) => {
        if (playerNames.length > 1) {
            const newPlayerNames = playerNames.filter((_, i) => i !== index);
            setPlayerNames(newPlayerNames);
            const newFormData = {};
            newPlayerNames.forEach((name, i) => {
                newFormData[`player_${i}`] = name;
            });
            form.setFieldsValue(newFormData);
        }
    };

    const updatePlayerName = (index, name) => {
        const newPlayers = [...playerNames];
        newPlayers[index] = name;
        setPlayerNames(newPlayers);
    };

    const generateMobileURL = (id) => {
        const baseURL = window.location.origin + window.location.pathname;
        return `${baseURL}?viewId=mobile&nfc=${id}`;
    };

    const handleStart = async () => {
        if (!selectedAvatar) {
            showWarning('Proszę wybrać awatar przed rejestracją! 🎭');
            return;
        }

        if (!groupName.trim()) {
            showWarning('Proszę wprowadzić nazwę grupy! 📝');
            return;
        }

        const validPlayerNames = playerNames
            .filter(name => name.trim() !== '')
            .map(name => name.trim());

        const uniquePlayerNames = [...new Set(validPlayerNames)];

        if (uniquePlayerNames.length === 0) {
            showWarning('Proszę wprowadzić przynajmniej jedno imię gracza! 👤');
            return;
        }

        if (uniquePlayerNames.length > 6) {
            showWarning('Maksymalnie 6 graczy w grupie! 👥');
            return;
        }

        if (uniquePlayerNames.length !== validPlayerNames.length) {
            showWarning('Imiona graczy muszą być unikalne! 🔄');
            return;
        }

        setLoading(true);
        showInfo(`${groupMode === "create" ? "Rejestrowanie" : "Aktualizowanie"} grupy "${groupName}" z ${uniquePlayerNames.length} graczami...`);

        try {
            await api.loginPlayer(groupId, selectedAvatar.numericId, groupName, uniquePlayerNames);
            showSuccess(`Grupa "${groupName}" została ${groupMode === "create" ? "zarejestrowana" : "zaktualizowana"}! ✅`);

            const team = {
                nfcTagId: groupId,
                avatar: selectedAvatar,
                groupName: groupName,
                playerNames: uniquePlayerNames,
                mobileURL: generateMobileURL(groupId),
            };

            setTeamData(team);
            setShowQRCode(true);

            setTimeout(() => {
                showInfo('Kod QR wygenerowany! Zeskanuj telefonem lub przejdź do gry 📱');
            }, 1000);

            localStorage.setItem("mobileTerminalUrl", team.mobileURL);

            // Nie czyścimy formularza - pozwalamy na dalszą edycję
        } catch (error) {
            showError(`Błąd ${groupMode === "create" ? "rejestracji" : "aktualizacji"}: ${error.message} ❌`);
        } finally {
            setLoading(false);
        }
    };

    // Auto-ukrywanie QR po 60s
    useEffect(() => {
        if (showQRCode) {
            const timer = setTimeout(() => {
                setShowQRCode(false);
            }, 60000);
            return () => clearTimeout(timer);
        }
    }, [showQRCode]);

    const isLoginFormValid = () => {
        return selectedAvatar &&
            groupName.trim() &&
            playerNames.some(name => name.trim());
    };

    const goToMobileTerminal = () => {
        if (teamData?.mobileURL) {
            window.location.href = teamData.mobileURL;
        }
    };

    return (
        <div className="container">
            <div className="overlay-gradient"></div>
            <div className="layout-grid" style={{position: 'relative', zIndex: 1}}>

                {/* Mobile Header */}
                <div className="grid-span-8" style={{textAlign: 'center', marginBottom: 24}}>
                    <Title level={1} className="telefon" style={{color: 'var(--color-white)', margin: 0}}>
                        <TeamOutlined style={{marginRight: 12, color: 'var(--color-light-green)'}} />
                        {groupMode === "select" ? "Wybierz Grupę" :
                            groupMode === "create" ? "Nowa Grupa" :
                                "Edytuj Grupę"}
                    </Title>
                    <Text style={{color: 'var(--white-60)', fontSize: '16px'}}>
                        {hasGroupIdFromUrl ? 'Z linku recepcji' : 'Zarządzanie grupami'}
                    </Text>
                </div>

                {/* Group ID Status */}
                {groupMode !== "select" && (
                    <div className="grid-span-8" style={{marginBottom: 24}}>
                        <Alert
                            message={hasGroupIdFromUrl ? "Grupa z recepcji" :
                                groupMode === "create" ? "Nowa grupa" : "Grupa wczytana"}
                            description={`Identyfikator Grupy: ${groupId}`}
                            type={hasGroupIdFromUrl ? "success" : groupMode === "create" ? "info" : "warning"}
                            icon={hasGroupIdFromUrl ? <QrcodeOutlined /> :
                                groupMode === "create" ? <PlusOutlined /> : <SearchOutlined />}
                            showIcon
                            action={!hasGroupIdFromUrl ? (
                                <Button size="small" onClick={handleBackToSelect}>
                                    Zmień
                                </Button>
                            ) : null}
                            style={{
                                backgroundColor: 'var(--white-10)',
                                border: `1px solid var(--color-${hasGroupIdFromUrl ? 'light-green' : groupMode === 'create' ? 'light-blue' : 'orange'})`,
                                color: 'var(--color-white)'
                            }}
                        />
                    </div>
                )}

                {/* Group Selection Mode */}
                {groupMode === "select" && (
                    <>
                        {/* Manual Group ID Input */}
                        <div className="grid-span-8" style={{marginBottom: 24}}>
                            <Card className="card">
                                <Title level={4} style={{color: 'var(--color-white)', textAlign: 'center', marginBottom: 20}}>
                                    <SearchOutlined style={{marginRight: 8}} />
                                    Wczytaj istniejącą grupę
                                </Title>

                                <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                                    <Input
                                        size="large"
                                        placeholder="Wprowadź Identyfikator Grupy"
                                        value={manualGroupId}
                                        onChange={(e) => setManualGroupId(e.target.value)}
                                        onPressEnter={handleLoadGroup}
                                        disabled={loading}
                                        style={{
                                            fontSize: '16px',
                                            textAlign: 'center'
                                        }}
                                    />
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={handleLoadGroup}
                                        disabled={loading || !manualGroupId.trim()}
                                        loading={loading}
                                        icon={<SearchOutlined />}
                                        style={{
                                            minWidth: '100px'
                                        }}
                                    >
                                        Wczytaj
                                    </Button>
                                </Space.Compact>

                                <Divider style={{ borderColor: 'var(--white-30)', margin: '20px 0' }}>
                                    <Text style={{ color: 'var(--white-60)', fontSize: '12px' }}>lub</Text>
                                </Divider>

                                <Button
                                    type="dashed"
                                    size="large"
                                    onClick={handleCreateNewGroup}
                                    icon={<PlusOutlined />}
                                    block
                                    style={{
                                        height: '44px',
                                        fontSize: '14px',
                                        borderColor: 'var(--color-light-green)',
                                        color: 'var(--color-light-green)'
                                    }}
                                >
                                    Utwórz nową grupę
                                </Button>
                            </Card>
                        </div>

                    </>
                )}

                {/* Group Form (Create/Edit Mode) */}
                {(groupMode === "create" || groupMode === "loaded") && (
                    <>
                        {/* Group Info Form */}
                        <div className="grid-span-8" style={{marginBottom: 24}}>
                            <Card className="card">
                                <Title level={4} style={{color: 'var(--color-white)', textAlign: 'center', marginBottom: 20}}>
                                    <TeamOutlined style={{marginRight: 8}} />
                                    Informacje o grupie
                                </Title>

                                <Form form={form} layout="vertical" onFinish={handleStart}>
                                    {/* Group Name */}
                                    <div style={{marginBottom: 20}}>
                                        <Text style={{color: 'var(--color-white)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: 8}}>
                                            Nazwa grupy:
                                        </Text>
                                        <Input
                                            size="large"
                                            placeholder="np. 'Drużyna Alfa'"
                                            value={groupName}
                                            onChange={(e) => setGroupName(e.target.value)}
                                            disabled={loading}
                                            maxLength={30}
                                            style={{
                                                fontSize: '16px',
                                                textAlign: 'center'
                                            }}
                                        />
                                    </div>

                                    {/* Players */}
                                    <div style={{marginBottom: 20}}>
                                        <Text style={{color: 'var(--color-white)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: 8}}>
                                            <UserOutlined style={{marginRight: 4}} />
                                            Gracze ({playerNames.filter(n => n.trim()).length}/6):
                                        </Text>

                                        <Row gutter={[8, 8]}>
                                            {playerNames.map((name, index) => (
                                                <Col xs={24} key={index}>
                                                    <div style={{
                                                        background: 'var(--white-10)',
                                                        borderRadius: 'var(--radius-8)',
                                                        padding: '8px',
                                                        border: '1px solid var(--white-30)',
                                                        display: 'flex',
                                                        gap: '8px',
                                                        alignItems: 'center'
                                                    }}>
                                                        <div style={{
                                                            background: 'var(--color-light-green)',
                                                            color: 'var(--color-white)',
                                                            borderRadius: '50%',
                                                            width: '24px',
                                                            height: '24px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold',
                                                            flexShrink: 0
                                                        }}>
                                                            {index + 1}
                                                        </div>
                                                        <Input
                                                            placeholder={`Imię gracza ${index + 1}`}
                                                            value={name}
                                                            onChange={(e) => updatePlayerName(index, e.target.value)}
                                                            disabled={loading}
                                                            maxLength={20}
                                                            size="large"
                                                            style={{
                                                                flex: 1,
                                                                fontSize: '16px'
                                                            }}
                                                        />
                                                        {playerNames.length > 1 && (
                                                            <Button
                                                                type="text"
                                                                onClick={() => removePlayerField(index)}
                                                                disabled={loading}
                                                                icon={<DeleteOutlined style={{color: '#ff4d4f'}} />}
                                                                style={{
                                                                    flexShrink: 0,
                                                                    border: 'none',
                                                                    background: 'transparent'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>

                                        {playerNames.length < 6 && (
                                            <Button
                                                type="dashed"
                                                onClick={addPlayerField}
                                                disabled={loading}
                                                icon={<PlusOutlined />}
                                                block
                                                size="large"
                                                style={{
                                                    marginTop: 12,
                                                    height: '44px',
                                                    fontSize: '14px',
                                                    borderColor: 'var(--white-40)'
                                                }}
                                            >
                                                Dodaj gracza
                                            </Button>
                                        )}
                                    </div>
                                </Form>
                            </Card>
                        </div>

                        {/* Avatar Selection */}
                        <div className="grid-span-8" style={{marginBottom: 24}}>
                            <Card className="card">
                                <Title level={4} style={{color: 'var(--color-white)', textAlign: 'center', marginBottom: 20}}>
                                    <TeamOutlined style={{marginRight: 8}} />
                                    Wybierz awatar grupy
                                </Title>

                                <Row gutter={[8, 8]} justify="center">
                                    {availableAvatars
                                        .filter(avatar => avatar.available)
                                        .map(avatar => (
                                            <Col xs={8} sm={6} md={4} key={avatar.id}>
                                                <div
                                                    onClick={() => setSelectedAvatar(avatar)}
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        padding: '12px 8px',
                                                        borderRadius: 'var(--radius-8)',
                                                        border: selectedAvatar?.id === avatar.id
                                                            ? '3px solid var(--color-light-green)'
                                                            : '2px solid var(--white-30)',
                                                        backgroundColor: selectedAvatar?.id === avatar.id
                                                            ? 'var(--white-20)'
                                                            : 'var(--white-10)',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        minHeight: '80px',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <AvatarDisplay
                                                        avatarId={avatar.numericId}
                                                        size={40}
                                                        showBorder={false}
                                                    />
                                                    <Text
                                                        style={{
                                                            color: 'var(--color-white)',
                                                            fontSize: '10px',
                                                            marginTop: '4px',
                                                            textAlign: 'center',
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {avatar.name || `#${avatar.numericId}`}
                                                    </Text>
                                                </div>
                                            </Col>
                                        ))}
                                </Row>
                            </Card>
                        </div>

                        {/* Registration/Update Button */}
                        {isLoginFormValid() && (
                            <div className="grid-span-8" style={{marginBottom: 24}}>
                                <Card className="card" style={{textAlign: 'center'}}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px',
                                        marginBottom: '16px'
                                    }}>
                                        <AvatarDisplay
                                            avatarId={selectedAvatar.numericId}
                                            size={32}
                                        />
                                        <div style={{textAlign: 'left'}}>
                                            <Text style={{color: 'var(--color-white)', fontSize: '14px', fontWeight: 'bold', display: 'block'}}>
                                                "{groupName}"
                                            </Text>
                                            <Text style={{color: 'var(--white-60)', fontSize: '12px'}}>
                                                {playerNames.filter(n => n.trim()).join(', ')}
                                            </Text>
                                        </div>
                                    </div>

                                    <Row gutter={[8, 8]}>
                                        {!hasGroupIdFromUrl && (
                                            <Col xs={24} sm={8}>
                                                <Button
                                                    onClick={handleBackToSelect}
                                                    disabled={loading}
                                                    size="large"
                                                    style={{
                                                        width: '100%',
                                                        height: '44px'
                                                    }}
                                                >
                                                    ← Wstecz
                                                </Button>
                                            </Col>
                                        )}
                                        <Col xs={24} sm={hasGroupIdFromUrl ? 24 : 16}>
                                            <Button
                                                type="primary"
                                                size="large"
                                                onClick={handleStart}
                                                disabled={loading}
                                                loading={loading}
                                                icon={<CheckOutlined />}
                                                className="button primary mobile-big"
                                                style={{
                                                    width: '100%',
                                                    height: '50px',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {loading ?
                                                    `${groupMode === "create" ? "Rejestrowanie" : "Aktualizowanie"}...` :
                                                    `${groupMode === "create" ? "Zarejestruj" : "Zaktualizuj"} grupę "${groupName}"`
                                                }
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>
                            </div>
                        )}
                    </>
                )}

                {/* Success & QR Code Section */}
                {showQRCode && teamData && (
                    <div className="grid-span-8" style={{marginBottom: 24}}>
                        <Card className="card">
                            <Title level={4} style={{color: 'var(--color-white)', textAlign: 'center', marginBottom: 20}}>
                                <CheckOutlined style={{marginRight: 8, color: 'var(--color-light-green)'}} />
                                Grupa {groupMode === "create" ? "zarejestrowana" : "zaktualizowana"}!
                            </Title>

                            <div style={{textAlign: 'center'}}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '12px',
                                    marginBottom: '16px'
                                }}>
                                    <AvatarDisplay
                                        avatarId={teamData.avatar.numericId}
                                        size={40}
                                    />
                                    <div style={{textAlign: 'left'}}>
                                        <Text style={{color: 'var(--color-white)', fontSize: '14px', fontWeight: 'bold', display: 'block'}}>
                                            "{teamData.groupName}"
                                        </Text>
                                        <Text style={{color: 'var(--white-60)', fontSize: '12px', display: 'block'}}>
                                            {teamData.playerNames?.join(', ')}
                                        </Text>
                                        <Text style={{color: 'var(--white-40)', fontSize: '10px'}}>
                                            ID: {teamData.nfcTagId}
                                        </Text>
                                    </div>
                                </div>

                                {/* QR Code */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginBottom: 16,
                                    padding: 16,
                                    backgroundColor: 'white',
                                    borderRadius: 8,
                                    width: 'fit-content',
                                    margin: '0 auto 16px'
                                }}>
                                    <QRCodeSVG
                                        value={teamData.mobileURL}
                                        size={150}
                                        level="M"
                                        includeMargin={true}
                                    />
                                </div>

                                {/* Action Buttons */}
                                <Row gutter={[8, 8]}>
                                    <Col xs={24}>
                                        <Button
                                            type="primary"
                                            size="large"
                                            onClick={goToMobileTerminal}
                                            icon={<MobileOutlined />}
                                            className="button primary"
                                            style={{
                                                width: '100%',
                                                height: '44px',
                                                fontSize: '14px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Przejdź do gry
                                        </Button>
                                    </Col>
                                    <Col xs={8}>
                                        <Button
                                            onClick={() => setShowQRCode(false)}
                                            icon={<EyeInvisibleOutlined />}
                                            className="button secondary"
                                            style={{
                                                width: '100%',
                                                height: '40px',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Ukryj QR
                                        </Button>
                                    </Col>
                                    <Col xs={8}>
                                        <Button
                                            onClick={() => {
                                                navigator.clipboard.writeText(teamData.mobileURL);
                                                showSuccess('Link skopiowany! 📋');
                                            }}
                                            icon={<CopyOutlined />}
                                            className="button secondary"
                                            style={{
                                                width: '100%',
                                                height: '40px',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Kopiuj
                                        </Button>
                                    </Col>
                                    <Col xs={8}>
                                        <Button
                                            onClick={handleBackToSelect}
                                            className="button secondary"
                                            style={{
                                                width: '100%',
                                                height: '40px',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Nowa
                                        </Button>
                                    </Col>
                                </Row>

                                <Text style={{color: 'var(--white-40)', fontSize: '10px', marginTop: 12, display: 'block', wordBreak: 'break-all'}}>
                                    {teamData.mobileURL}
                                </Text>
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegisterPlayers;